import { useState, type ReactNode } from 'react';
import { X } from 'lucide-react';
import {
  noteLabel,
  useDismissNoteSource,
  useFormNoteSources,
  type NoteSource,
  type NoteSourceStatus,
} from '../../../lib/api/formNotes';
import { wordDiff } from './wordDiff';

/**
 * Slice 19c, option A from the mockups: the Form notes drawer beside the
 * report editor. One card per criterion item in this section, showing its
 * note on the form and where it stands in the report.
 *
 * The drawer never writes narrative itself. Inserting, keeping and removing
 * go through the editor (onInsert and friends), so the author's text is only
 * ever changed by the author's own save.
 */
export function FormNotesDrawer({
  tenantId,
  auditId,
  sectionKey,
  editable,
  busy,
  onInsert,
  onReplace,
  onGoTo,
  onClose,
}: {
  tenantId: string;
  auditId: string;
  sectionKey: string;
  editable: boolean;
  /** An insert/keep/remove is in flight in the editor. */
  busy: boolean;
  onInsert: (source: NoteSource) => void;
  /** Swap a pending block for the note's current text. */
  onReplace: (source: NoteSource) => void;
  onGoTo: (insertionId: string) => void;
  onClose: () => void;
}) {
  const sources = useFormNoteSources(tenantId, auditId, sectionKey, true);
  const dismiss = useDismissNoteSource(tenantId, auditId);

  return (
    <aside className="w-[22rem] shrink-0 bg-white border border-gray-200 rounded-lg overflow-hidden
                      flex flex-col max-h-[calc(100vh-8rem)]"
           aria-label="Form notes">
      <header className="flex items-start justify-between gap-3 px-4 py-3 border-b border-gray-200">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-gray-900">Form notes</h3>
          {sources.data && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">
              Section {sources.data.sectionCode} · {sources.data.sectionName} · {sources.data.items.length} items
            </p>
          )}
        </div>
        <button type="button" onClick={onClose} title="Close form notes"
                className="text-gray-500 hover:bg-gray-100 rounded-md w-7 h-7
                           inline-flex items-center justify-center">
          <X className="w-4 h-4" aria-hidden />
        </button>
      </header>

      <div className="overflow-y-auto p-3 space-y-2.5">
        {sources.isLoading && <p className="text-sm text-gray-500 px-1">Loading notes…</p>}
        {sources.error && <p className="text-sm text-danger-500 px-1">Couldn't load the form notes.</p>}
        {sources.data?.items.length === 0 && (
          <p className="text-sm text-gray-500 px-1">No items in this section apply to this audit.</p>
        )}
        {sources.data?.items.map(source => (
          <NoteCard key={source.criterionItemId}
                    source={source}
                    editable={editable}
                    busy={busy || dismiss.isPending}
                    onInsert={() => onInsert(source)}
                    onReplace={() => onReplace(source)}
                    onGoTo={onGoTo}
                    onDismiss={() => dismiss.mutate(source.criterionItemId)} />
        ))}
      </div>
    </aside>
  );
}

function NoteCard({
  source, editable, busy, onInsert, onReplace, onGoTo, onDismiss,
}: {
  source: NoteSource;
  editable: boolean;
  busy: boolean;
  onInsert: () => void;
  onReplace: () => void;
  onGoTo: (insertionId: string) => void;
  onDismiss: () => void;
}) {
  const [showingChanges, setShowingChanges] = useState(false);
  const inserted = source.latestInsertion;
  const attention = source.status === 'Changed';

  return (
    <article className={
      'rounded-md border px-3 py-2.5 ' +
      (attention ? 'border-amber-300 bg-amber-50' : 'border-gray-200 bg-white')
    }>
      <header className="flex items-start gap-2">
        <span className="text-xs font-semibold text-gray-500 mt-0.5 shrink-0">{noteLabel(source)}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 leading-snug">{source.itemText}</p>
          <p className="text-[11px] text-gray-500 truncate">{source.criterionName}</p>
        </div>
        <StatusBadge status={source.status} />
      </header>

      <div className="mt-2 text-sm text-gray-700">
        {source.note
          ? <p className="whitespace-pre-line">{source.note}</p>
          : source.status === 'NoNote' && <p className="italic text-gray-400">No note on the form yet.</p>}
      </div>

      {showingChanges && inserted && (
        <ChangesView before={inserted.sourceTextAtInsert} after={source.note ?? ''} />
      )}

      <footer className="mt-2 flex flex-wrap items-center gap-2 text-sm">
        {source.status === 'NotUsed' && editable && (
          <CardButton onClick={onInsert} disabled={busy} primary>Add to report</CardButton>
        )}

        {source.status === 'AwaitingReview' && inserted && (
          <>
            <button type="button" onClick={() => onGoTo(inserted.insertionId)}
                    className="text-accent-alt-700 underline hover:text-accent-alt-800">
              Go to it in the report
            </button>
            {source.sourceChangedSinceInsert && editable && (
              <>
                <p className="w-full text-xs text-amber-800">
                  The note has changed on the form since this was inserted.
                </p>
                <CardButton onClick={() => setShowingChanges(v => !v)}>
                  {showingChanges ? 'Hide changes' : 'View changes'}
                </CardButton>
                <CardButton onClick={onReplace} disabled={busy}>Replace with current version</CardButton>
              </>
            )}
          </>
        )}

        {source.status === 'Changed' && (
          <>
            <CardButton onClick={() => setShowingChanges(v => !v)}>
              {showingChanges ? 'Hide changes' : 'View changes'}
            </CardButton>
            {editable && <CardButton onClick={onInsert} disabled={busy}>Add updated version</CardButton>}
            {editable && <CardButton onClick={onDismiss} disabled={busy} quiet>Dismiss</CardButton>}
          </>
        )}

        {source.status === 'Added' && inserted && (
          <p className="text-xs text-gray-500">
            Added {formatDate(inserted.insertedAtUtc)}
            {inserted.insertedByDisplayName && <> by {inserted.insertedByDisplayName}</>}
          </p>
        )}

        {source.status === 'Removed' && (
          <>
            <p className="w-full text-xs text-gray-600">
              The note was cleared on the form. The text you kept is still in the report.
            </p>
            {editable && <CardButton onClick={onDismiss} disabled={busy} quiet>Dismiss</CardButton>}
          </>
        )}

        {source.status === 'NoLongerApplies' && (
          <>
            <p className="w-full text-xs text-gray-600">
              An answer on the form now hides this item. The text you kept is still in the report.
            </p>
            {editable && <CardButton onClick={onDismiss} disabled={busy} quiet>Dismiss</CardButton>}
          </>
        )}
      </footer>
    </article>
  );
}

const STATUS_STYLE: Record<NoteSourceStatus, { label: string; className: string }> = {
  NoNote:          { label: 'No note',          className: 'border-gray-200 text-gray-500' },
  NotUsed:         { label: 'Not used',         className: 'border-gray-300 text-gray-700' },
  AwaitingReview:  { label: 'Awaiting review',  className: 'border-blue-200 bg-blue-50 text-blue-700' },
  Added:           { label: 'Added',            className: 'border-gray-200 bg-gray-50 text-gray-700' },
  Changed:         { label: 'Changed',          className: 'border-amber-300 bg-amber-100 text-amber-800' },
  Removed:         { label: 'Note removed',     className: 'border-gray-300 text-gray-700' },
  NoLongerApplies: { label: 'No longer applies', className: 'border-gray-300 text-gray-700' },
};

function StatusBadge({ status }: { status: NoteSourceStatus }) {
  const style = STATUS_STYLE[status];
  return (
    <span className={'shrink-0 text-[11px] font-medium px-2 py-0.5 rounded-full border ' + style.className}>
      {style.label}
    </span>
  );
}

function CardButton({
  children, onClick, disabled, primary, quiet,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
  quiet?: boolean;
}) {
  const look = primary
    ? 'bg-accent-alt-600 text-white hover:bg-accent-alt-700 border-transparent'
    : quiet
      ? 'border-transparent text-gray-700 hover:bg-gray-100'
      : 'border-gray-300 bg-white text-gray-800 hover:bg-gray-50';
  return (
    <button type="button" onClick={onClick} disabled={disabled}
            className={'px-2.5 py-1 rounded-md border text-sm font-medium transition-colors ' +
                       'disabled:opacity-40 disabled:cursor-not-allowed ' + look}>
      {children}
    </button>
  );
}

/** The note as inserted against the note now, word by word. */
function ChangesView({ before, after }: { before: string; after: string }) {
  return (
    <div className="mt-2 rounded border border-gray-200 bg-white px-2.5 py-2 text-sm leading-relaxed">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1">
        Changes since it was inserted
      </p>
      <p className="whitespace-pre-line">
        {wordDiff(before, after).map((part, i) =>
          part.kind === 'same'
            ? <span key={i}>{part.text}</span>
            : part.kind === 'added'
              ? <ins key={i} className="bg-green-100 text-green-900 no-underline">{part.text}</ins>
              : <del key={i} className="bg-red-100 text-red-800">{part.text}</del>)}
      </p>
    </div>
  );
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}
